import React, { useState, useEffect, ChangeEvent } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import Card from '../../../../components/Card';
import { DetailItem } from '../../../../features/details';
import { DraftTerminalContract, hasContractChanged } from '../../../../features/TerminalContracts/TerminalContractDraft';
import { useTerminalContractsDraft } from '../../../../features/TerminalContracts/TerminalContractsDraftContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPropertyInfo } from '../../../../services/api/terminalContract';
import { PropertyInfo } from '../../../../services/openapi/models/PropertyInfo';
import { TerminalContract } from '../../../../services/openapi';
import { useKeycloak } from '@react-keycloak/web';

const TerminalContractDetails = () => {
    const [showInputEle, setShowInputEle] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [draft, setDraft] = useState<DraftTerminalContract | null>(null);
    const [fetchedPropertyNames, setFetchedPropertyNames] = useState<{ [key: string]: PropertyInfo } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getDraftByKey, upsertDraft, getDraftForHost, getUsedBrands } = useTerminalContractsDraft();

    const navigateBack = () => {
        navigate('/dashboard/lists/terminal-contracts');
    };

    useEffect(() => {
        const loadPropertyInfo = async () => {
            try {
                const draftKey = localStorage.getItem('TerminalContractDraftKey') ?? '';
                const existingDraft = getDraftByKey(draftKey);
                const poi = existingDraft?.current != null ? existingDraft?.current.id == 0 ? existingDraft?.current.poi : null : null;
                const result = await getPropertyInfo(keycloak.token || '', poi);
                console.log('getPropertyInfo result', result);
                setFetchedPropertyNames(result);
            } catch (error) {
                console.error('Failed to load property info', error);
                setErrorMsg([t('common.loadFailed') || 'Failed to load property metadata']);
            }
        };

        if (!fetchedPropertyNames) {
            loadPropertyInfo();
        }
    }, [keycloak.token, fetchedPropertyNames, t]);

    useEffect(() => {
        if (!fetchedPropertyNames || draft !== null) {
            return;
        }

        const draftKey = localStorage.getItem('TerminalContractDraftKey') ?? '';

        if (!draftKey) {
            setIsLoading(false);
            return;
        }

        const existingDraft = getDraftByKey(draftKey);

        if (existingDraft) {
            setDraft({
                ...existingDraft,
                current: {
                    ...existingDraft.current,
                    propertyNames:
                        existingDraft.current.propertyNames &&
                        Object.keys(existingDraft.current.propertyNames).length > 0
                            ? existingDraft.current.propertyNames
                            : fetchedPropertyNames
                }
            });
            setIsLoading(false);

            if (existingDraft.current.id === 0 && existingDraft.current.brand === '') {
                toggleInputElements(true);
            }

            return;
        }

        const newDraft: DraftTerminalContract = {
            key: draftKey,
            mode: 'new',
            original: null,
            current: {
                id: 0,
                terminalId: 0,
                poi: '',
                terminalDescription: '',
                brand: '',
                tid: '',
                mid: '',
                host: '',
                isActive: 'False',
                propertyNames: fetchedPropertyNames,
                warnings: null
            } as TerminalContract
        };

        setDraft(newDraft);
        setIsLoading(false);
    }, [draft, fetchedPropertyNames, getDraftByKey]);

    const toggleInputElements = (show: boolean) => {
        setShowInputEle(show);
        if (show) {
            document.querySelector('.errorMsg')?.classList.add('d-none');
            setErrorMsg([]);
        }
    };

    const updateField = (key: string, value: string) => {
        setDraft(prev => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                current: {
                    ...prev.current,
                    [key]: value
                }
            };
        });
    };

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        document.querySelector('.errorMsg')?.classList.add('d-none');
        setErrorMsg([]);

        if (!e.currentTarget.checkValidity()) {
            return;
        }

        if (!draft) {
            return;
        }
        
        draft.current.warnings = hasContractChanged(draft) ? null : draft.current.warnings;
        upsertDraft(draft);
        setShowInputEle(false);
        navigateBack();
    }

    if (isLoading) {
        return (
            <Row>
                <Col>
                    <Card className="rounded">
                        <Card.Body>
                            <div>Loading...</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    }

    if (!draft) {
        return (
            <Row>
                <Col>
                    <Card className="rounded">
                        <Card.Body>
                            <Alert variant="warning">
                                No draft found for terminal contract.
                            </Alert>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    }

    const termContract = draft.current;

    const propertyNames =
        termContract.propertyNames &&
        Object.keys(termContract.propertyNames).length > 0
            ? termContract.propertyNames
            : fetchedPropertyNames ?? {};
    propertyNames["brand"].originalPredefinedValues ??= propertyNames["brand"].predefinedValues;        
    propertyNames["brand"].predefinedValues = propertyNames["brand"].originalPredefinedValues.filter((value) => !getUsedBrands().includes(value) || value === termContract.brand);
    return (
        <>
            <Row>
                <Col>
                    <Card className="rounded">
                        <Card.Body>
                            <form onSubmit={handleSubmit}>
                                <Row className="d-none d-md-block">
                                    <Col>&nbsp;</Col>
                                </Row>
                                <Row className="d-none d-md-block">
                                    <Col>&nbsp;</Col>
                                </Row>

                                <Row>
                                    <Col sm="12">
                                        <h4 className="mb-2 qvend-title">Terminal Contract</h4>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm="12" xs="12" className="mt-2">
                                        <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                            <div>
                                                {Object.entries(propertyNames)
                                                .filter(([key, prop]) => {
                                                    const value = termContract[key as keyof typeof termContract];
                                                    return prop !== undefined;
                                                })
                                                .sort(
                                                    ([a], [b]) =>
                                                        (propertyNames[a].sortOrderLevel ?? 0) -
                                                        (propertyNames[b].sortOrderLevel ?? 0)
                                                )
                                                .map(([key, prop], index) => {
                                                    const rawValue = termContract[key as keyof typeof termContract];
                                                    const stringValue = rawValue == null ? '' : String(rawValue);
                                                    const boolValue =                                                        
                                                        rawValue === 'true' ||
                                                        rawValue === 'True';

                                                    return (
                                                        <DetailItem
                                                            key={prop.sortOrderLevel ?? index}
                                                            propertyName={prop.name}
                                                            defaultValue={rawValue ?? ''}
                                                            value={prop.editType !== 'checkbox' ? stringValue : undefined}
                                                            checked={prop.editType === 'checkbox' ? boolValue : undefined}
                                                            rowColor={index % 2 ? '#cfd5ea' : '#e9ebf5'}
                                                            editType={prop.editType}
                                                            pattern={prop.valPattern}
                                                            hideOnNull={prop.hideOnNull}
                                                            handleDoubleClick={() => setShowInputEle(true)}
                                                            handleChange={(
                                                            e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
                                                            ) => {
                                                            const newValue =
                                                                prop.editType === 'checkbox'
                                                                ? (e.target as HTMLInputElement).checked
                                                                    ? 'True'
                                                                    : 'False'
                                                                : e.target.value;

                                                            // Always update the field the user changed.
                                                            updateField(key, newValue);

                                                            // Only initialize related fields when the host changes
                                                            // for a new contract.
                                                            if (                                                                
                                                                key === 'host'
                                                            ) {
                                                                const selectedHost = String(newValue);

                                                                const draft = getDraftForHost(selectedHost);

                                                                updateField(
                                                                'tid',
                                                                draft?.current?.host === selectedHost
                                                                    ? draft.current.tid ?? ''
                                                                    : ''
                                                                );

                                                                updateField(
                                                                'mid',
                                                                draft?.current?.host === selectedHost
                                                                    ? draft.current.mid ?? ''
                                                                    : ''
                                                                );
                                                            }
                                                            }}
                                                            showInputEle={showInputEle}
                                                            possibilities={prop.predefinedValues}
                                                            enumMap={prop.enumMap}
                                                            description={prop.description}
                                                            conditionToShowReferenceValue={termContract[termContract["propertyNames"][key].conditionToShowFieldName]}                                                            
                                                            conditionToShowValue={prop.conditionToShowValue}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className={errorMsg.length > 0 ? 'errorMsg' : 'd-none errorMsg'}>
                                    <Col>
                                        {errorMsg.map((item, index) => (
                                            <Alert key={index} variant="warning" className="alert" role="alert">
                                                <span>{item}</span>
                                            </Alert>
                                        ))}
                                    </Col>
                                </Row>

                                <Row style={{ paddingTop: '12px' }}>
                                    <Col>
                                        <button
                                            type="button"
                                            className="btn btn-fixed-width"
                                            style={{ fontSize: '11px', backgroundColor: 'red' }}
                                            onClick={navigateBack}
                                        >
                                            {t('button.cancel')}
                                        </button>
                                    </Col>

                                    <Col className="text-end">
                                        {showInputEle ? (
                                            <button type="submit" className="btn btn-fixed-width" style={{ fontSize: '11px' }}>
                                                {termContract?.id && termContract.id > 0
                                                    ? t('button.update')
                                                    : t('button.add')}
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-fixed-width"
                                                style={{ fontSize: '11px' }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    toggleInputElements(true);
                                                }}
                                            >
                                                {t('button.edit')}
                                            </button>
                                        )}
                                    </Col>
                                </Row>
                            </form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default TerminalContractDetails;